import { useMemo } from 'react';

import uniqBy from 'lodash-es/uniqBy';
import { UseFormReturn, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { providerNameMapper } from '@/modules/developerTools/pages/languageModels/helper';
import { ProviderName } from '@/modules/developerTools/types';

type LLMSelectorProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form?: UseFormReturn<any>;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
};

/**
 * Provider and model selector for LLM configuration.
 *
 * Renders two comboboxes: one for provider selection and one for model selection.
 * When a provider is selected, the model automatically updates to the first available model.
 *
 * Expects the form to have `provider` and `language_model_id` fields.
 *
 * @behavior
 * - Returns `null` when no custom language models are enabled (only Maestro available)
 * - Shows a skeleton when `isLoading` is true and options haven't loaded yet
 * - Can be used inside a FormProvider (no props needed) or standalone (pass `form` prop)
 *
 * @example
 * // Inside FormProvider
 * <LLMSelector />
 *
 * // Standalone
 * <LLMSelector form={form} disabled={!canWrite} isLoading />
 */
export const LLMSelector = ({
  form: formProp,
  disabled = false,
  isLoading = false,
  className,
}: LLMSelectorProps) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formContext = useFormContext<any>();
  const { control, setValue } = formProp ?? formContext;

  const { enabledModels, enabledModelsWithMoveo } = useLanguageModels();

  const provider = useWatch({ control, name: 'provider' });

  const llms = useMemo(
    () => (enabledModels.length > 0 ? enabledModelsWithMoveo : []),
    [enabledModels.length, enabledModelsWithMoveo]
  );

  const uniqueProviders = useMemo(() => uniqBy(llms, 'provider'), [llms]);

  const providerOptions = useMemo(
    () =>
      uniqueProviders.map((model) => ({
        value: model.provider,
        label: providerNameMapper(model.provider as ProviderName),
      })),
    [uniqueProviders]
  );

  const modelOptions = useMemo(
    () =>
      llms
        .filter((model) => model.provider === provider)
        .map((model) => ({ value: model.language_model_id as string, label: model.model })),
    [llms, provider]
  );

  const hasSelectedProvider = provider !== '';

  const handleProviderChange = (value: string, fieldOnChange: (value: string) => void) => {
    fieldOnChange(value);
    const firstModelOption = uniqueProviders.find((m) => m.provider === value);
    setValue('language_model_id', firstModelOption?.language_model_id ?? 'Default', {
      shouldDirty: true,
    });
  };

  // Show skeleton while loading
  if (isLoading && providerOptions.length === 0) {
    return <Skeleton className="h-[84px] w-full" />;
  }

  // Hide the component entirely if no custom models are enabled
  if (enabledModels.length === 0) {
    return null;
  }

  return (
    <div className={className ?? 'flex gap-4'}>
      <FormField
        control={control}
        name="provider"
        render={({ field, fieldState }) => (
          <FormItem className="w-full">
            <FormLabel>{t('field.provider')}</FormLabel>
            <FormControl>
              <PopoverCombobox
                options={providerOptions}
                value={field.value ?? ''}
                onChange={(value) => handleProviderChange(value, field.onChange)}
                triggerPlaceholder={t('common.select_an_option')}
                error={!!fieldState.error}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="language_model_id"
        render={({ field, fieldState }) => (
          <FormItem className="w-full">
            <FormLabel>{t('field.model')}</FormLabel>
            <FormControl>
              <PopoverCombobox
                options={modelOptions}
                value={field.value ?? ''}
                onChange={field.onChange}
                triggerPlaceholder={t('common.select_an_option')}
                disabled={!hasSelectedProvider || disabled}
                error={!!fieldState.error}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
